import React, {useContext, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    CssBaseline,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {alpha, ThemeProvider} from "@mui/material/styles";
import {useNavigate} from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {useMutation} from "react-query";

import kaproscmLogo from "../assets/KaproSCMIcon.svg";
import semenarnaLogo from "../assets/semenarna.png";
import Copyright from "../components/Copyright";
import Spinner from "../components/Spinner";
import LanguageSwitch from "../components/LanguageSwitch";
import DarkModeSwitch from "../components/DarkModeSwitch";
import SupplyChainBackdrop from "../components/SupplyChainBackdrop";
import {LanguageContext} from "../components/LanguageContext";
import {darkTheme, lightTheme} from "../theme";
import {loginApi} from "../api/authApi";

const LoginPage = () => {
    const navigate = useNavigate();
    const {translations} = useContext(LanguageContext);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [spinner, setSpinner] = useState(true);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState(
        () => localStorage.getItem("theme") === "dark" ? "dark" : "light"
    );

    const theme = mode === "dark" ? darkTheme : lightTheme;

    const handleToggleTheme = () => {
        setMode((prev) => {
            const next = prev === "dark" ? "light" : "dark";
            localStorage.setItem("theme", next);
            return next;
        });
    };

    const mutation = useMutation({
        mutationFn: (data) => loginApi(data),
        onSuccess: (response) => {
            setLoading(false);
            localStorage.setItem("token", response.data.token);
            localStorage.setItem("user_id", response.data.user.id);
            localStorage.setItem("store_id", response.data.user.store_id);
            localStorage.setItem("store_code", response.data.user.store_code);
            const typeMap = {O: "Owner", A: "Administrator"};
            const userType = (response.data.user.type || "").toUpperCase();
            localStorage.setItem("role", typeMap[userType] || "Korisnik");
            localStorage.setItem("user_type", userType);
            navigate("/pocetna");
        },
        onError: (err) => {
            setError(err.response?.data?.message || "");
            setLoading(false);
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        const formData = new FormData(e.target);
        mutation.mutateAsync(formData).catch(() => {
        });
    };

    useEffect(() => {
        const t = setTimeout(() => setSpinner(false), 1500);
        return () => clearTimeout(t);
    }, []);

    if (spinner) return <Spinner/>;

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline/>
            <Box
                sx={(t) => ({
                    minHeight: "100vh",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: {xs: 2, sm: 3, md: 5},
                    py: {xs: 3, md: 5},
                    background: `
                        radial-gradient(circle at 12% 18%, ${alpha(
                        t.palette.primary.main,
                        t.palette.mode === "dark" ? 0.18 : 0.1
                    )} 0%, transparent 45%),
                        radial-gradient(circle at 88% 82%, ${alpha(
                        t.palette.secondary.main,
                        t.palette.mode === "dark" ? 0.16 : 0.08
                    )} 0%, transparent 45%),
                        ${t.palette.background.default}
                    `,
                })}
            >
                <SupplyChainBackdrop opacity={mode === "dark" ? 0.4 : 0.55} animate/>

                <Box
                    sx={{
                        position: "absolute",
                        top: {xs: 16, md: 24},
                        right: {xs: 16, md: 24},
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        zIndex: 2,
                    }}
                >
                    <Tooltip title={translations.toggleTheme || "Toggle theme"}>
                        <Box>
                            <DarkModeSwitch
                                onChange={handleToggleTheme}
                                checked={mode === "dark"}
                            />
                        </Box>
                    </Tooltip>
                    <LanguageSwitch/>
                </Box>

                <Box
                    sx={(t) => ({
                        position: "relative",
                        zIndex: 1,
                        width: "100%",
                        maxWidth: 1080,
                        bgcolor: "background.paper",
                        borderRadius: {xs: 3, md: 4},
                        border: `1px solid ${t.palette.divider}`,
                        boxShadow:
                            t.palette.mode === "dark"
                                ? "0 1px 2px rgba(0,0,0,0.4), 0 16px 40px rgba(0,0,0,0.45)"
                                : "0 1px 2px rgba(15,23,42,0.04), 0 16px 40px rgba(15,23,42,0.06)",
                        overflow: "hidden",
                    })}
                >
                    <Grid container>
                        <Grid
                            item
                            xs={12}
                            md={6}
                            sx={{
                                p: {xs: 3, sm: 4, md: 6},
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                            }}
                        >
                            <Box
                                component="img"
                                src={kaproscmLogo}
                                alt="KaproSCM"
                                sx={{
                                    height: {xs: 48, sm: 56},
                                    alignSelf: "flex-start",
                                    mb: {xs: 3, sm: 4},
                                    filter: theme.palette.mode === "dark"
                                        ? "brightness(1.15)"
                                        : "none",
                                }}
                            />

                            <Stack spacing={0.75} sx={{mb: 3}}>
                                <Typography variant="h3" component="h1">
                                    {translations.loginPageTitle}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {translations.welcomeText}
                                </Typography>
                            </Stack>

                            {mutation.isError && (
                                <Alert severity="error" sx={{mb: 2}}>
                                    {error || translations.loginPageError}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit} noValidate>
                                <Stack spacing={2}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="credentials"
                                        name="credentials"
                                        label={translations.loginPageEmailLabel}
                                        type="email"
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />

                                    <FormControl variant="outlined" fullWidth required>
                                        <InputLabel htmlFor="login-password">
                                            {translations.loginPagePasswordLabel}
                                        </InputLabel>
                                        <OutlinedInput
                                            id="login-password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            label={translations.loginPagePasswordLabel}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={() =>
                                                            setShowPassword((v) => !v)
                                                        }
                                                        onMouseDown={(e) =>
                                                            e.preventDefault()
                                                        }
                                                        edge="end"
                                                    >
                                                        {showPassword ? (
                                                            <VisibilityOff/>
                                                        ) : (
                                                            <Visibility/>
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        />
                                    </FormControl>

                                    <Button
                                        type="submit"
                                        fullWidth
                                        size="large"
                                        variant="contained"
                                        disabled={loading}
                                        startIcon={
                                            loading ? (
                                                <CircularProgress
                                                    color="inherit"
                                                    size={18}
                                                />
                                            ) : null
                                        }
                                        sx={{mt: 1}}
                                    >
                                        {translations.loginPageSubmitButton}
                                    </Button>
                                </Stack>
                            </Box>

                            <Box sx={{mt: 3}}>
                                <Copyright sx={{m: 0, textAlign: "center"}}/>
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={6}
                            sx={(t) => ({
                                display: {xs: "none", md: "flex"},
                                alignItems: "center",
                                justifyContent: "center",
                                p: 6,
                                position: "relative",
                                background: `
                                    radial-gradient(circle at 30% 20%, ${alpha(
                                    t.palette.primary.main,
                                    t.palette.mode === "dark" ? 0.28 : 0.18
                                )} 0%, transparent 55%),
                                    radial-gradient(circle at 80% 80%, ${alpha(
                                    t.palette.secondary.main,
                                    t.palette.mode === "dark" ? 0.26 : 0.18
                                )} 0%, transparent 55%),
                                    linear-gradient(135deg, ${alpha(
                                    t.palette.primary.main,
                                    t.palette.mode === "dark" ? 0.1 : 0.06
                                )} 0%, ${alpha(
                                    t.palette.secondary.main,
                                    t.palette.mode === "dark" ? 0.1 : 0.06
                                )} 100%)
                                `,
                                borderLeft: `1px solid ${t.palette.divider}`,
                            })}
                        >
                            <Box
                                component="img"
                                src={semenarnaLogo}
                                alt="Semenarna"
                                sx={{
                                    maxWidth: "70%",
                                    maxHeight: 280,
                                    width: "auto",
                                    height: "auto",
                                    objectFit: "contain",
                                    filter: theme.palette.mode === "dark"
                                        ? "drop-shadow(0 8px 24px rgba(0,0,0,0.4)) brightness(1.05)"
                                        : "invert(1) drop-shadow(0 8px 24px rgba(15,23,42,0.12))",
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default LoginPage;